import { FormControl, FormLabel } from "@chakra-ui/form-control";
import { Flex } from "@chakra-ui/layout";
import {
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
} from "@chakra-ui/number-input";
import {
  FormErrorMessage,
  FormHelperText,
  InputGroup,
  useColorMode,
  useColorModeValue,
} from "@chakra-ui/react";
import {
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
} from "@chakra-ui/slider";
import { at } from "lodash";
import React from "react";
import useFieldFast from "../../hooks/useFieldFast";
import useInProgressDisable from "../../hooks/useInProgressDisable";
import { darkMode, lightMode } from "../../lib/models/color-mode-colors";

export type InputFieldProps = {
  label: string;
  name: string;
  errorText?: string;
  helperText?: string;
  defaultValue: number;
  step: number;
  max: number;
  min: number;
  precision?: number;
  disabled?: boolean;
  inputFieldDisable?: boolean;
};

function SliderField(props: InputFieldProps) {
  const {
    label,
    name,
    helperText,
    defaultValue,
    step,
    max,
    min,
    precision,
    disabled,
    inputFieldDisable
  } = props;
  const [field, meta, helpers] = useFieldFast(props);
  const { value } = field;
  const { setValue } = helpers;

  const [inputValue, setInputValue] = React.useState(+value);
  const handleChange = (value: any) => setInputValue(value);
  const handleChangeEnd = (value: any) => setFieldValue(value);
  const handleInputChange = (value: any) => {
    setFieldValue(value);
  };
  const isDisabled = useInProgressDisable(name) || disabled;
  const { colorMode } = useColorMode();
  const sliderThumbBg = useColorModeValue(lightMode[100], darkMode[200]);

  const setFieldValue = React.useCallback(
    (value) => {
      setValue(value);
    },
    [setValue]
  );

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  function renderHelperText() {
    const [touched, error] = at(meta, "touched", "error");

    if (touched && error) {
      return error;
    }
  }

  return (
    <FormControl id={name} isDisabled={isDisabled}>
      <FormLabel>{label}</FormLabel>
      <InputGroup>
        <Flex width="full">
          <NumberInput
            defaultValue={defaultValue}
            step={step}
            max={max}
            min={min}
            precision={precision ?? 2}
            maxW="100px"
            mr="2rem"
            value={inputValue}
            onChange={handleInputChange}
          >
            <NumberInputField disabled={inputFieldDisable} />
            <NumberInputStepper>
              <NumberIncrementStepper />
              <NumberDecrementStepper />
            </NumberInputStepper>
          </NumberInput>
          <Slider
            flex="1"
            value={inputValue}
            focusThumbOnChange={false}
            isDisabled={isDisabled}
            onChange={(val: number) => {
              handleChange(val);
            }}
            onChangeEnd={(val: number) => {
              handleChangeEnd(val);
            }}
            defaultValue={defaultValue}
            step={step}
            max={max}
            disabled={disabled}
            data-testid={`${label}-slider`}
            min={min}
          >
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb
              border={colorMode === "dark" ? "1px solid" : "none"}
              borderColor={colorMode === "dark" ? "white" : ""}
              background={sliderThumbBg}
              fontSize="sm"
              w="60px"
              h="33px"
              borderRadius="md"
              children={<span>{inputValue} %</span>}
            />
          </Slider>
        </Flex>
      </InputGroup>
      {meta.touched && meta.error ? (
        <FormErrorMessage>{renderHelperText()}</FormErrorMessage>
      ) : (
        helperText && <FormHelperText>{helperText}</FormHelperText>
      )}
    </FormControl>
  );
}

export default SliderField;
